import { getOfflineDb } from './dbFactory'
import { getActiveTenantKey } from './tenant'
import { dexieFarmRepo } from './repos/DexieFarmRepo'
import { dexieLotRepo } from './repos/DexieLotRepo'
import { dexieTaskRepo } from './repos/DexieTaskRepo'
import { dexieInventoryItemRepo } from './repos/DexieInventoryItemRepo'
import { readFarmsFromLocalStorage, writeFarmsToLocalStorage } from './farms/farmsLocalStorageMirror'
import { readLotsFromLocalStorage, writeLotsToLocalStorage } from './lots/lotsLocalStorageMirror'
import { readTasksFromLocalStorage, writeTasksToLocalStorage } from './tasks/tasksLocalStorageMirror'
import { loadInventoryItems } from '../utils/inventoryStorage'
import { clearRetiredSamplingSidecar } from './sidecarCleanup'

const offlineReadyByTenant = new Map<string, Promise<void>>()

const migrateTasksIfNeeded = async (tenantKey: string) => {
  const offlineDb = getOfflineDb(tenantKey)
  const existingCount = await offlineDb.tasks.count()
  if (existingCount > 0) return

  const legacy = readTasksFromLocalStorage()
  if (legacy.length === 0) return

  await dexieTaskRepo.upsertMany(legacy)
}

const migrateFarmsIfNeeded = async (tenantKey: string) => {
  const offlineDb = getOfflineDb(tenantKey)
  const existingCount = await offlineDb.farms.count()
  if (existingCount > 0) return

  const legacy = readFarmsFromLocalStorage()
  if (legacy.length === 0) return

  await dexieFarmRepo.upsertMany(legacy)
}

const migrateLotsIfNeeded = async (tenantKey: string) => {
  const offlineDb = getOfflineDb(tenantKey)
  const existingCount = await offlineDb.lots.count()
  if (existingCount > 0) return

  const legacy = readLotsFromLocalStorage()
  if (legacy.length === 0) return

  await dexieLotRepo.upsertMany(legacy)
}

const migrateInventoryIfNeeded = async (tenantKey: string) => {
  const offlineDb = getOfflineDb(tenantKey)
  const existingCount = await offlineDb.inventoryItems.count()
  if (existingCount > 0) return

  // Uses tenant-scoped waterStorage key under the active tenant.
  const legacy = loadInventoryItems()
  if (legacy.length === 0) return

  await dexieInventoryItemRepo.upsertMany(legacy)
}

// Phase 6 decision (2026-02-16): localStorage mirroring is on-demand only.
// We keep explicit sync helpers for export/import tooling, but do not mirror automatically
// during startup or normal UI flows.

export const ensureOfflineReady = async (): Promise<void> => {
  const tenantKey = getActiveTenantKey()
  const existing = offlineReadyByTenant.get(tenantKey)
  if (existing) return existing

  const promise = (async () => {
    const offlineDb = getOfflineDb(tenantKey)
    // Opening is implicit, but this ensures Dexie initializes early.
    await offlineDb.open()
    await migrateTasksIfNeeded(tenantKey)
    await migrateFarmsIfNeeded(tenantKey)
    await migrateLotsIfNeeded(tenantKey)
    await migrateInventoryIfNeeded(tenantKey)
    // v2.1 finding #60 (WSL-08, 2026-07-01): wipe the retired
    // `production:samplingAggregate:v1` localStorage sidecar per tenant.
    // Guarded and idempotent — see `sidecarCleanup.ts`.
    clearRetiredSamplingSidecar()
  })()

  offlineReadyByTenant.set(tenantKey, promise)
  return promise
}

export const syncTasksFromLocalStorageIntoDb = async (): Promise<void> => {
  await ensureOfflineReady()
  const tasks = readTasksFromLocalStorage()
  await dexieTaskRepo.replaceAll(tasks)
}

export const syncTasksFromDbIntoLocalStorage = async (): Promise<void> => {
  await ensureOfflineReady()
  const tasks = await dexieTaskRepo.list()
  writeTasksToLocalStorage(tasks)
}

export const syncFarmsFromLocalStorageIntoDb = async (): Promise<void> => {
  await ensureOfflineReady()
  const farms = readFarmsFromLocalStorage()
  await dexieFarmRepo.replaceAll(farms)
}

export const syncFarmsFromDbIntoLocalStorage = async (): Promise<void> => {
  await ensureOfflineReady()
  const farms = await dexieFarmRepo.list()
  writeFarmsToLocalStorage(farms)
}

export const syncLotsFromLocalStorageIntoDb = async (): Promise<void> => {
  await ensureOfflineReady()
  const lots = readLotsFromLocalStorage()
  await dexieLotRepo.replaceAll(lots)
}

export const syncLotsFromDbIntoLocalStorage = async (): Promise<void> => {
  await ensureOfflineReady()
  const lots = await dexieLotRepo.list()
  writeLotsToLocalStorage(lots)
}
